import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Clock } from 'lucide-react';
import { TASK_CATEGORIES } from '@/lib/constants';
import { TaskStatus, TaskPriority } from '@/lib/types/enums';
import type { Task } from '@/lib/types';

interface TaskListProps {
  onTaskClick: (task: Task) => void;
}

// Mock data
const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Fix Leaking Faucet',
    description: 'Kitchen sink faucet is leaking and needs immediate repair',
    status: TaskStatus.PENDING,
    priority: TaskPriority.HIGH,
    category: 'handyman',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
    dueDate: '2024-01-16T17:00:00Z',
    propertyId: 'prop1',
  },
  {
    id: '2',
    title: 'Weekly Garden Maintenance',
    description: 'Regular garden maintenance including mowing and trimming',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    category: 'landscaping',
    createdAt: '2024-01-14T09:00:00Z',
    updatedAt: '2024-01-14T15:30:00Z',
    dueDate: '2024-01-14T16:00:00Z',
    propertyId: 'prop2',
  },
];

export function TaskList({ onTaskClick }: TaskListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const filteredTasks = mockTasks.filter((task) => {
    const matchesSearch = task.title.toLowerCase().includes(search.toLowerCase()) ||
      task.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tasks</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.values(TaskStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {status.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {TASK_CATEGORIES.map((category) => (
                <SelectItem key={category.value} value={category.value}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredTasks.map((task) => (
            <div
              key={task.id}
              onClick={() => onTaskClick(task)}
              className="flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <div>
                <h3 className="font-medium">{task.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {task.description}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge
                    variant={
                      task.status === TaskStatus.COMPLETED ? 'default' :
                      task.status === TaskStatus.IN_PROGRESS ? 'secondary' :
                      task.status === TaskStatus.PENDING ? 'outline' :
                      'destructive'
                    }
                  >
                    {task.status.replace('_', ' ')}
                  </Badge>
                  <Badge
                    variant={
                      task.priority === TaskPriority.HIGH ? 'destructive' :
                      task.priority === TaskPriority.MEDIUM ? 'secondary' :
                      'outline'
                    }
                  >
                    {task.priority} priority
                  </Badge>
                  <Badge variant="outline">
                    {task.category}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}